import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ImageIcon, X } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, resolveImageUrl } from "../lib/config";

const EDITIONS = [
  { id: "prima", label: "Prima Edizione" },
  { id: "seconda", label: "Seconda Edizione" },
  { id: "terza", label: "Terza Edizione" },
];

export default function GalleryPage() {
  const [edition, setEdition] = useState("terza");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/gallery?edition=${edition}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [edition]);

  return (
    <div className="min-h-screen bg-fp-bg" data-testid="gallery-page">
      <Navbar />

      <section className="relative pt-32 pb-16 px-5 md:px-8 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 radial-glow" />
        <div className="relative max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-fp-cyan text-sm mb-8 transition-colors">
            <ArrowLeft size={16} /> Torna alla home
          </Link>

          <div className="text-center mb-12">
            <div className="text-xs tracking-[0.4em] uppercase text-fp-cyan font-heading font-semibold">
              I momenti del torneo
            </div>
            <h1 className="mt-4 font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tight">
              <span className="silver-gradient">Gallery</span>
            </h1>
            <p className="mt-5 text-white/60 max-w-2xl mx-auto">
              Le foto delle edizioni passate del Fanta Partenope.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {EDITIONS.map((e) => (
              <button
                key={e.id}
                onClick={() => setEdition(e.id)}
                className={`px-6 py-3 rounded-full text-sm font-heading font-semibold tracking-wider uppercase transition-all ${
                  edition === e.id
                    ? "bg-fp-cyan text-fp-bg shadow-[0_0_20px_rgba(0,229,255,0.5)]"
                    : "border border-white/10 text-white/70 hover:border-fp-cyan/50 hover:text-fp-cyan"
                }`}
                data-testid={`gallery-tab-${e.id}`}
              >
                {e.label}
              </button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="text-center text-white/50 py-20">Caricamento...</div>
          ) : items.length === 0 ? (
            <div className="max-w-md mx-auto text-center py-20">
              <ImageIcon className="mx-auto text-white/20 mb-4" size={56} />
              <h3 className="font-heading font-bold text-xl text-white/70">Nessuna foto disponibile</h3>
              <p className="mt-2 text-white/50 text-sm">Le foto saranno pubblicate a breve dall'organizzazione.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {items.map((it, i) => (
                <motion.button
                  key={it.id || i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => setLightbox(it)}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10 bg-fp-surface/40 hover:border-fp-cyan/50 transition-all"
                  data-testid={`gallery-item-${i}`}
                >
                  <img
                    src={resolveImageUrl(it.image_url)}
                    alt={it.title || "Foto"}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-fp-bg/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  {it.title && (
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="text-white font-heading font-semibold text-sm">{it.title}</div>
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-5"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 w-12 h-12 rounded-full bg-white/10 hover:bg-fp-cyan hover:text-fp-bg flex items-center justify-center transition-all"
          >
            <X size={20} />
          </button>
          <motion.img
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            src={resolveImageUrl(lightbox.image_url)}
            alt={lightbox.title}
            className="max-w-full max-h-[90vh] rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
